// Local storage wrappers. Phase 1: identity + friends + groups + messages
// all live in localStorage so they survive app restarts on the phone. The
// Supabase auth session also persists itself (under its own key).

import type { Identity, Friend, Group, Message } from '../types';

// v2 = post linked-devices reshape. v1 keys are obsolete — their Identity
// shape predates per-device keys, and Friend records hold pubkeys that no
// longer correspond to anything on the relay. Wiping at boot is cleaner
// than trying to migrate.
const IDENTITY_KEY = 'norush.identity.v2';
const FRIENDS_KEY = 'norush.friends.v2';
const GROUPS_KEY = 'norush.groups.v2';
const MESSAGES_KEY = 'norush.messages.v2';
const GROUP_MESSAGES_KEY = 'norush.group-messages.v2';
// Deliberately NOT in clearAll: the age-gate outcome must survive
// "Sign out and wipe" so a blocked under-age user can't retry by wiping,
// and a passed adult isn't re-asked. We store only the one-bit outcome —
// no birth date is ever asked for or kept (data minimization).
const AGE_GATE_KEY = 'norush.agegate.v1';

// One-shot purge of v1 keys so old installs auto-clean on first v2 boot.
// Safe to call repeatedly; later boots find nothing to remove.
(function purgeLegacyKeys() {
  if (typeof localStorage === 'undefined') return;
  const legacy = [
    'norush.identity.v1',
    'norush.friends.v1',
    'norush.groups.v1',
    'norush.messages.v1',
    'norush.group-messages.v1',
  ];
  for (const k of legacy) {
    if (localStorage.getItem(k) !== null) localStorage.removeItem(k);
  }
})();

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  localStorage.setItem(key, JSON.stringify(value));
}

export type AgeGateOutcome =
  | { passedAt: string }
  | { failedAt: string };

export function saveAgeGate(outcome: AgeGateOutcome): void {
  write(AGE_GATE_KEY, outcome);
}

export function loadAgeGate(): AgeGateOutcome | null {
  return read<AgeGateOutcome>(AGE_GATE_KEY);
}

export function saveIdentity(identity: Identity): void {
  write(IDENTITY_KEY, identity);
}

export function loadIdentity(): Identity | null {
  return read<Identity>(IDENTITY_KEY);
}

export function clearAll(): void {
  for (const key of [
    IDENTITY_KEY,
    FRIENDS_KEY,
    GROUPS_KEY,
    MESSAGES_KEY,
    GROUP_MESSAGES_KEY,
  ]) {
    localStorage.removeItem(key);
  }
}

export function saveFriends(friends: Friend[]): void {
  write(FRIENDS_KEY, friends);
}

export function loadFriends(): Friend[] {
  return read<Friend[]>(FRIENDS_KEY) ?? [];
}

export function saveGroups(groups: Group[]): void {
  write(GROUPS_KEY, groups);
}

export function loadGroups(): Group[] {
  return read<Group[]>(GROUPS_KEY) ?? [];
}

export function saveMessages(byFriend: Record<string, Message[]>): void {
  write(MESSAGES_KEY, byFriend);
}

export function loadMessages(): Record<string, Message[]> {
  return read<Record<string, Message[]>>(MESSAGES_KEY) ?? {};
}

export function saveGroupMessages(byGroup: Record<string, Message[]>): void {
  write(GROUP_MESSAGES_KEY, byGroup);
}

export function loadGroupMessages(): Record<string, Message[]> {
  return read<Record<string, Message[]>>(GROUP_MESSAGES_KEY) ?? {};
}
